import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { resolveImg } from "@/lib/api";
import { X } from "@phosphor-icons/react";

const EASE = [0.22, 1, 0.36, 1];

function youTubeId(url = "") {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : null;
}
function vimeoId(url = "") {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return m ? m[1] : null;
}

export default function VideoLightbox({ project, onClose }) {
  const video = (project?.videos || []).filter(Boolean)[0];
  const yt = video && youTubeId(video);
  const vm = video && vimeoId(video);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!video) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        data-testid="video-lightbox"
        className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-10"
        style={{ background: "rgba(24,20,17,0.92)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        onClick={onClose}
      >
        <button
          data-testid="video-lightbox-close"
          onClick={onClose}
          aria-label="Close video"
          className="absolute top-5 right-5 md:top-8 md:right-8 z-10 h-11 w-11 rounded-full border border-white/30 text-white/80 grid place-items-center hover:bg-white hover:text-black transition-colors duration-300"
        >
          <X size={20} weight="light" />
        </button>

        <motion.div
          className="relative w-full max-w-[1280px]"
          initial={{ opacity: 0, scale: 0.82, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.55, ease: EASE }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative w-full overflow-hidden rounded-lg bg-black shadow-2xl" style={{ aspectRatio: "16 / 9" }}>
            {yt || vm ? (
              <iframe
                data-testid="video-lightbox-iframe"
                title={project.title}
                className="absolute inset-0 h-full w-full"
                src={
                  yt
                    ? `https://www.youtube.com/embed/${yt}?autoplay=1&controls=1&modestbranding=1&rel=0&playsinline=1`
                    : `https://player.vimeo.com/video/${vm}?autoplay=1`
                }
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                data-testid="video-lightbox-player"
                className="absolute inset-0 h-full w-full object-contain"
                src={resolveImg(video)}
                poster={resolveImg(project.cover_image)}
                autoPlay
                controls
                playsInline
              />
            )}
          </div>
          <div className="mt-5 flex items-baseline justify-between gap-4">
            <p className="font-serif font-light text-xl md:text-2xl text-white">{project.title}</p>
            {project.location && <p className="text-xs md:text-sm text-white/60">{project.location}</p>}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
